import { Dashboard, Workspace, WorkspaceDashboard } from "models/workspace";

export interface DashboardPositionResult {
  position: number;
  isAddingToSelected: boolean;
}

/**
 * Calculates the position where a cloned dashboard should be inserted.
 * - If original is selected: clone is inserted after it in the workspace selected list
 * - If original is not selected: clone is added to the end of the selected dashboards list
 *
 * @param dashboardToClone - The dashboard being cloned
 * @param workspace - The workspace containing the selected dashboard list
 * @returns Object containing position and whether to add to selected dashboards
 */
export function calculateCloneDashboardPosition(
  dashboardToClone: Dashboard | null,
  workspace: Workspace | null | undefined
): DashboardPositionResult {
  if (!dashboardToClone || !workspace?.dashboards) {
    return {
      position: 0,
      isAddingToSelected: false
    };
  }

  // Check if the original dashboard is in the selected dashboards
  const originalDashboardIndex = workspace.dashboards.findIndex(
    (wd: WorkspaceDashboard | null) => wd?.dashboardId === dashboardToClone.dashboardId
  );

  const isOriginalInSelected = originalDashboardIndex !== -1;

  if (isOriginalInSelected) {
    // Original is selected: insert clone right after it in the selected list
    return {
      position: originalDashboardIndex + 1,
      isAddingToSelected: true
    };
  } else {
    // Original is not selected: clone should be added to the END of selected dashboards
    return {
      position: workspace.dashboards.length, // Add to the end of selected dashboards
      isAddingToSelected: true
    };
  }
}

export interface CloneContext {
  originalDashboardId: string;
  clonedDashboardId: string;
  isAddingToSelected: boolean;
  position: number;
  isScrolling: boolean;
  isHighlighting: boolean;
}

/**
 * Creates clone context information for scroll and highlight behavior
 *
 * @param dashboardToClone - The original dashboard being cloned
 * @param clonedDashboardId - The ID of the newly created clone
 * @param workspace - The workspace context
 * @returns CloneContext with behavior information
 */
export function createCloneContext(
  dashboardToClone: Dashboard | null,
  clonedDashboardId: string,
  workspace: Workspace | null | undefined,
  options: { isScrolling?: boolean; isHighlighting?: boolean } = {}
): CloneContext {
  const { isScrolling = true, isHighlighting = true } = options;

  if (!dashboardToClone || !workspace) {
    return {
      originalDashboardId: "",
      clonedDashboardId,
      isAddingToSelected: false,
      position: 0,
      isScrolling,
      isHighlighting
    };
  }

  const positionResult = calculateCloneDashboardPosition(dashboardToClone, workspace);

  return {
    originalDashboardId: dashboardToClone.dashboardId,
    clonedDashboardId,
    isAddingToSelected: positionResult.isAddingToSelected,
    position: positionResult.position,
    isScrolling,
    isHighlighting
  };
}
